import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';
import { Observable, of, map, catchError } from 'rxjs';
import { StateService } from './state.service';
import { UtilityService } from './utility.service';
import { Resource, Bundle } from 'fhir/r4';
import { SuccessToastService } from './success-toast.service';
import { ResourceRefreshService } from './resource-refresh.service';

interface PostOpts {
    patientId?: string;
    encounterId?: string;
    performerId?: string;
    defaultStatus?: string;
    codeFieldNames?: string[];
    referenceFieldNames?: string[];
}

@Injectable({ providedIn: 'root' })
export class FhirResourceService {
    private http = inject(HttpClient);
    private baseUrl = inject(backendEndPointToken);
    private state = inject(StateService);
    private util = inject(UtilityService);
    private successToast = inject(SuccessToastService);
    private resourceRefresh = inject(ResourceRefreshService);

    postResource(
        resourceType: string,
        values: any,
        opts?: PostOpts
    ): Observable<{ saved: boolean; resource: Resource }> {
        const resource = this.util.buildFhirResource(resourceType, values, opts) as Resource;

        return this.http.post<Resource>(`${this.baseUrl}/${resourceType}`, resource, {
            headers: {
                'Content-Type': 'application/fhir+json',
                Accept: 'application/fhir+json',
                Prefer: 'return=representation'
            }
        }).pipe(
            map((resp) => {
                const finalRes = { ...resource, ...resp };
                this.state.persistLocalResource(finalRes, 'saved');
                this.successToast.success(`${resourceType} saved successfully.`);
                return { saved: true, resource: finalRes };
            }),
            catchError(err => {
                console.error('POST resource failed:', err);
                this.state.persistLocalResource(resource, 'unsaved');
                return of({ saved: false, resource });
            })
        );
    }

    postBundle(bundle: Bundle): Observable<{ saved: boolean; bundle: Bundle; headers?: any }> {
        return this.http.post<Bundle>(this.baseUrl, bundle, {
            headers: {
                'Content-Type': 'application/fhir+json',
                Accept: 'application/fhir+json',
                Prefer: 'return=representation'
            },
            observe: 'response'
        }).pipe(
            map(resp => {
                const responseBundle = resp.body as Bundle;

                const shouldAlsoPersistOrgWide = (b: Bundle | null | undefined): boolean => {
                    const entries = b?.entry ?? [];
                    return entries.some((e: any) => {
                        const rt = e?.resource?.resourceType;
                        return rt === 'MedicationRequest' || rt === 'ServiceRequest';
                    });
                };

                if (this.state.currentPatientId$.getValue()) {
                    this.state.processBundleTransaction(responseBundle);

                    // If the user later navigates to an org-wide view (e.g., pharmacy dashboard),
                    // ensure newly-created MedicationRequests/ServiceRequests appear immediately.
                    if (shouldAlsoPersistOrgWide(responseBundle)) {
                        this.state.processOrgWideBundleTransaction(responseBundle);
                    }
                } else {
                    console.warn('No patient in state to process bundle transaction response.');
                    this.state.processOrgWideBundleTransaction(responseBundle);
                }

                this.successToast.success('Saved successfully.');
                try {
                    const entries = responseBundle?.entry ?? [];
                    const hasMedicationRequest = entries.some((e: any) => (e?.resource?.resourceType) === 'MedicationRequest');
                    if (hasMedicationRequest) {
                        // try { this.resourceRefresh.notify('medicine-requests', { full: false }); } catch (e) { }
                    }
                } catch (e) { }

                return {
                    saved: true,
                    bundle: responseBundle,
                    headers: resp.headers
                };
            }),
            catchError((err: any) => {
                console.error('POST bundle failed:', err);
                throw err;
            })
        );
    }
}